import { useParams, Link, Navigate } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleBySlug, getRelatedArticles, categories } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article.slug, article.categorySlug) : [];
  const category = article ? categories.find((c) => c.slug === article.categorySlug) : null;

  if (!article) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <SchemaOrg type="Article" article={{
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        datePublished: article.date,
        dateModified: article.date,
        authorName: article.author,
        url: `https://www.laosege.com/article/${slug}`,
      }} />
      <SchemaOrg type="WebPage" data={{
        name: article.title,
        description: article.excerpt,
        url: `https://www.laosege.com/article/${slug}`,
      }} />

      <main>
        <article>
          <div className="bg-background-100/50 border-b border-background-200/50">
            <div className="max-w-4xl mx-auto px-4 md:px-6 pt-20 md:pt-24 pb-0">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <div className="mb-6">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-3 py-1 text-xs font-medium bg-primary-100/80 text-primary-700 rounded-full mb-4"
              >
                {article.category}
              </Link>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <span className="font-medium text-foreground-800">{article.author}</span>
                </div>
                <span>{article.date}</span>
                <span>{article.readTime}</span>
              </div>
            </div>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto rounded-lg"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-loose prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-700 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between py-4 border-t border-background-200/70">
              <div>
                <div className="text-xs text-foreground-500 mb-1">作者</div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{article.author}</div>
                    <div className="text-xs text-foreground-500">老色哥社区达人</div>
                  </div>
                </div>
              </div>
              <Link
                to={category ? `/category/${category.slug}` : '/'}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
              >
                查看更多{article.category}内容 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/50 border-t border-background-200/50">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.slug} article={rel} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}